import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import {
    Formik, Form, FormikProps, FormikTouched,
} from 'formik';
import { ProFormaModelIds } from 'constants/proForma';
import { useProFormaWizard } from 'views/ProFormaWizard/context/ProFormaWizardProvider';
import { stepTwoValidationSchema } from 'views/ProFormaWizard/constants';
import { useParams } from 'react-router-dom';
import { numeralFormatter } from 'ns_libs/formatter';
import { useAuthContext } from 'contexts/AuthContext';
import { isEmpty } from 'lodash';
import RenderIf from 'components/RenderIf/RenderIf';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { IProFormaWizardStep1, IProFormaWizardStep2, IWizardStepTwoUses } from 'views/ProFormaWizard/types';
import { Deal } from 'views/Deals/types';
import { formatStepTwoData } from 'views/ProFormaWizard/helpers/formatStepData';
import { UnitTypeIds } from 'constants/unitTypes';
import { getHardCostsAndTotals } from 'views/ProFormaWizard/helpers';
import { COST_BASIS_TYPES } from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import { getDevelopmentDiscrepancies } from 'views/ProFormaTable/helpers';
import { useGetSourceTypes } from 'queries/ProFormaSource';
import { isDebtSource, isEquitySource } from 'utils/sourceTypeUtils';
import Uses from './Uses/Uses';
import { ISource } from './Sources/Source/types';
import { sourcesInitialState } from './Sources/Source/constants';
import Sources from './Sources/Sources';
import { useGetProFormaWizardStep } from '../../hooks/useGetProFormaWizardStep';
import { useUpdateProFormaWizardStep } from '../../hooks/useUpdateProFormaWizardStep';

export type StepTwoValues = IWizardStepTwoUses & {
    sources: ISource[];
};

export interface IProFormaWizardStepTwoProps {
    deal?: Deal;
    boeScenario?: BOEScenario;
}

interface StepTwoFormContentProps {
    formik: FormikProps<StepTwoValues>;
    registerStepHandler: ReturnType<typeof useProFormaWizard>['registerStepHandler'];
    proFormaId: string;
    updateStep: ReturnType<typeof useUpdateProFormaWizardStep>;
    selectedOrganizationId: string;
    wizardBuiltGrossSF: number;
    dealUnits: number;
    proFormaModelId: number;
    sourceTypes: ReturnType<typeof useGetSourceTypes>['data'];
    boeScenario?: BOEScenario;
}

const StepTwoFormContent = ({
    formik,
    registerStepHandler,
    proFormaId,
    updateStep,
    selectedOrganizationId,
    wizardBuiltGrossSF,
    dealUnits,
    proFormaModelId,
    sourceTypes,
    boeScenario,
}: StepTwoFormContentProps) => {
    const {
        values, setFieldValue, validateForm, errors, touched, setFieldTouched, setTouched,
    } = formik;

    const getData = useCallback(() => {
        const data = formatStepTwoData(values as any);
        return new Promise((resolve, reject) => {
            updateStep.mutate({
                orgId: selectedOrganizationId,
                proFormaId,
                stepNumber: 2,
                data,
            }, {
                onSuccess: result => resolve(result),
                onError: error => reject(error),
            });
        });
    }, [values, proFormaId, updateStep, selectedOrganizationId]);

    useEffect(() => {
        registerStepHandler(2, {
            validate: async () => {
                const validationErrors = await validateForm();
                if (Object.keys(validationErrors).length > 0) {
                    const touchedFields = Object.keys(validationErrors).reduce<FormikTouched<StepTwoValues>>((acc, key) => {
                        if (key === 'sources' && Array.isArray(validationErrors.sources)) {
                            const sourcesTouched = validationErrors.sources.map(sourceError => {
                                if (!sourceError) return {};
                                return Object.keys(sourceError).reduce((sourceAcc, field) => ({
                                    ...sourceAcc,
                                    [field]: true,
                                }), {});
                            });
                            acc.sources = sourcesTouched as FormikTouched<ISource>[];
                        } else {
                            (acc as any)[key] = true;
                        }
                        return acc;
                    }, {} as FormikTouched<StepTwoValues>);
                    setTouched(touchedFields);
                    return false;
                }
                return true;
            },
            getData,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerStepHandler, validateForm, getData]);

    const handleInputChange = (key: string, value: any) => {
        setFieldValue(key, value);
        const newTouched: FormikTouched<StepTwoValues> = {
            ...touched,
            [key]: false,
        };

        if (key === 'hardCosts') {
            newTouched.totalHardCosts = false;
        } else if (key === 'totalHardCosts') {
            newTouched.hardCosts = false;
        } else if (key === 'softCosts') {
            newTouched.totalSoftCosts = false;
        } else if (key === 'totalSoftCosts') {
            newTouched.softCosts = false;
        }

        setTouched(newTouched);
    };

    const handleSourcesChange = (newSources: ISource[]) => {
        setFieldValue('sources', newSources);
        const newTouched: FormikTouched<StepTwoValues> = { ...touched };
        if (newTouched.sources) {
            newTouched.sources = newSources.map(() => ({})) as FormikTouched<ISource>[];
        }
        setTouched(newTouched);
    };

    const handleSourcesBlur = (index: number, field: string) => {
        setFieldTouched(`sources.${index}.${field}`, false, false);
    };

    const totalUses = values.acquisitionCosts
        + values.totalHardCosts + values.totalSoftCosts + values.interestReserve + values.developerFee;

    const totalSources = useMemo(
        () => (values.sources as any).reduce((acc: number, curr: ISource) => acc + Number(curr.amount), 0), [values.sources],
    );

    const totalEquity = values.sources
        ?.filter(source => isEquitySource(source.sourceTypeId, sourceTypes))
        ?.reduce((acc, source) => acc + Number(source.amount), 0) || 0;

    const totalDebt = values.sources
        ?.filter(source => isDebtSource(source.sourceTypeId, sourceTypes))
        ?.reduce((acc, source) => acc + Number(source.amount), 0) || 0;

    const boeDiscrepancies = boeScenario
        ? getDevelopmentDiscrepancies(totalUses, totalEquity, totalDebt, boeScenario)
        : undefined;

    return (
        <Form>
            <Uses
                values={values}
                onChange={handleInputChange}
                onBlur={() => { }}
                wizardBuiltGrossSF={wizardBuiltGrossSF}
                dealUnits={dealUnits}
                proFormaModelId={proFormaModelId}
                totalSources={totalSources}
                errors={errors}
                touched={touched}
                boeDiscrepancies={boeDiscrepancies}
            />
            <Sources
                sources={values.sources as any}
                onChange={handleSourcesChange}
                onBlur={handleSourcesBlur}
                totalUses={totalUses}
                totalSources={totalSources}
                errors={errors.sources}
                touched={touched.sources}
                boeDiscrepancies={boeDiscrepancies}
            />
        </Form>
    );
};

const ProFormaWizardStepTwo = ({ deal, boeScenario }: IProFormaWizardStepTwoProps) => {
    const {
        registerStepHandler,
        getStepData,
        proForma,
        isLoading: isLoadingProvider,
    } = useProFormaWizard();
    const { proFormaId } = useParams<{ dealId: string; proFormaId: string }>();
    const { selectedOrganizationId } = useAuthContext();

    const stepOneState = getStepData(1);

    const { data: step1Data } = useGetProFormaWizardStep(selectedOrganizationId!, proFormaId || '', 1,
        { enabled: isEmpty(stepOneState) }) as { data: IProFormaWizardStep1 | undefined };

    const stepOneData = !isEmpty(stepOneState) ? stepOneState : step1Data;

    const {
        data: stepData,
        isLoading,
    } = useGetProFormaWizardStep(
        selectedOrganizationId!, proFormaId || '', 2,
    ) as { data: IProFormaWizardStep2 | undefined, isLoading: boolean };

    const dealUnits = deal?.dealUnitTypes?.find(unit => unit.unitType === UnitTypeIds.RESIDENTIAL_UNIT);
    const wizardBuiltGrossSF = stepOneData?.builtGrossSquareFootage || 0;
    const boeHardCostsUnitType = proForma?.boeScenario?.hardCostsSubdiv || COST_BASIS_TYPES.PER_SF;
    const boeSoftCostsUnitType = proForma?.boeScenario?.softCostsSubdiv || COST_BASIS_TYPES.PER_SF;

    const updateStep = useUpdateProFormaWizardStep();
    const { data: sourceTypes } = useGetSourceTypes();

    const [initialValues, setInitialValues] = useState<StepTwoValues>({
        acquisitionCosts: 0,
        hardCosts: 0,
        totalHardCosts: 0,
        hardCostType: COST_BASIS_TYPES.PER_SF,
        softCosts: 0,
        totalSoftCosts: 0,
        softCostType: COST_BASIS_TYPES.PER_SF,
        developerFeePercent: 0,
        developerFee: 0,
        interestReserve: 0,
        sources: sourcesInitialState,
    });

    useEffect(() => {
        const {
            hardCosts,
            totalHardCosts,
            softCosts,
            totalSoftCosts,
        } = getHardCostsAndTotals(
            boeHardCostsUnitType, boeSoftCostsUnitType,
            wizardBuiltGrossSF, stepData, dealUnits,
        );

        setInitialValues({
            acquisitionCosts: Number(numeralFormatter(Number(stepData?.acquisitionCosts || 0), '0.00')),
            hardCosts,
            totalHardCosts,
            hardCostType: boeHardCostsUnitType === COST_BASIS_TYPES.PER_UNIT ? COST_BASIS_TYPES.PER_UNIT : COST_BASIS_TYPES.PER_SF,
            softCosts,
            totalSoftCosts,
            softCostType: boeSoftCostsUnitType === COST_BASIS_TYPES.PER_UNIT ? COST_BASIS_TYPES.PER_UNIT : COST_BASIS_TYPES.PER_SF,
            developerFeePercent: 0,
            developerFee: Number(numeralFormatter(stepData?.developerFee ?? 0, '0.00')),
            interestReserve: Number(numeralFormatter(stepData?.interestReserve ?? 0, '0.00')),
            sources: (stepData?.sources || []).map(source => ({ ...source, ltcAmount: '' })) as any,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepData, stepOneData, dealUnits]);

    const proFormaModelId = proForma?.proFormaModelId || ProFormaModelIds.APARTMENT;

    return (
        <RenderIf isTrue={stepData} isLoading={isLoading || !stepData}>
            <ReadOnlyWrapper isReadOnly={isLoadingProvider}>
                <Formik
                    initialValues={initialValues as StepTwoValues}
                    validationSchema={stepTwoValidationSchema}
                    context={{ sourceTypes }}
                    onSubmit={() => { }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validateOnMount={false}
                    enableReinitialize
                >
                    {formik => (
                        <StepTwoFormContent
                            formik={formik}
                            registerStepHandler={registerStepHandler}
                            proFormaId={proFormaId || ''}
                            updateStep={updateStep}
                            selectedOrganizationId={selectedOrganizationId!}
                            wizardBuiltGrossSF={wizardBuiltGrossSF}
                            dealUnits={dealUnits?.value || 0}
                            proFormaModelId={proFormaModelId}
                            sourceTypes={sourceTypes}
                            boeScenario={boeScenario}
                        />
                    )}
                </Formik>
            </ReadOnlyWrapper>
        </RenderIf>
    );
};

export default ProFormaWizardStepTwo;
